import { PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';

// Exponential backoff helper
function getBackoffDelay(attemptIndex: number): number {
  // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
  const baseDelay = 1000;
  const maxDelay = 30000;
  const delay = Math.min(baseDelay * Math.pow(2, attemptIndex), maxDelay);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 1000;
  return delay + jitter;
}

// Determine if error should trigger a retry
function shouldRetry(error: unknown, failureCount: number): boolean {
  // Don't retry on 4xx errors (client errors)
  if (error instanceof Error) {
    const errorName = error.name.toLowerCase();
    const errorMessage = error.message.toLowerCase();

    // Don't retry on authentication/authorization errors
    if (
      errorName.includes('unauthorized') ||
      errorName.includes('forbidden') ||
      errorMessage.includes('401') ||
      errorMessage.includes('403')
    ) {
      return false;
    }

    // Don't retry on validation errors
    if (errorName.includes('validation') || errorName.includes('zoderror')) {
      return false;
    }

    // Retry on network errors, timeouts, and server errors
    if (
      errorName.includes('network') ||
      errorName.includes('timeout') ||
      errorName.includes('server') ||
      errorMessage.includes('500') ||
      errorMessage.includes('502') ||
      errorMessage.includes('503') ||
      errorMessage.includes('504')
    ) {
      return failureCount < 3; // Max 3 retries
    }
  }

  // Default: retry up to 3 times for unknown errors
  return failureCount < 3;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry configuration
      retry: (failureCount, error) => shouldRetry(error, failureCount),
      retryDelay: (attemptIndex) => getBackoffDelay(attemptIndex),

      // Stale time and cache time
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Refetch configuration
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once for network errors
      retry: (failureCount, error) => {
        if (error instanceof Error) {
          const errorName = error.name.toLowerCase();

          // Only retry network/timeout errors, not auth or validation
          if (
            (errorName.includes('network') || errorName.includes('timeout')) &&
            failureCount < 1
          ) {
            return true;
          }
        }
        return false;
      },
      retryDelay: (attemptIndex) => getBackoffDelay(attemptIndex),
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Global error handler for queries
      // Only show toast for non-auth errors (auth errors are handled in routes)
      if (error instanceof Error) {
        const errorName = error.name.toLowerCase();
        const errorMessage = error.message.toLowerCase();

        // Don't show toast for auth errors - they're handled in routes
        if (
          !errorName.includes('unauthorized') &&
          !errorMessage.includes('401') &&
          !errorMessage.includes('not authenticated')
        ) {
          // Only show error toast if query has no error handler
          // This prevents duplicate error messages
          if (!query.meta?.skipErrorToast) {
            toast.error('Failed to load data', {
              description: error.message || 'An error occurred while fetching data.',
            });
          }
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Global error handler for mutations
      // Only show toast if mutation doesn't have its own error handler
      if (error instanceof Error && !mutation.options.onError) {
        const errorName = error.name.toLowerCase();
        const errorMessage = error.message.toLowerCase();

        // Don't show toast for auth errors
        if (!errorName.includes('unauthorized') && !errorMessage.includes('401')) {
          toast.error('Operation failed', {
            description: error.message || 'An error occurred. Please try again.',
          });
        }
      }
    },
  }),
});

export function QueryProvider({ children }: PropsWithChildren) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
